import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import api from "services/api";

import iosIntro1 from "assets/images/clients/intros/ios/app-store-shared-id.png";

function safeDetail(data, fallback, lang) {
  if (!data) return fallback;
  if (typeof data === "string") return data;

  const isZh = typeof lang === "string" && lang.toLowerCase().startsWith("zh");
  const d = typeof data.detail === "string" ? data.detail : "";
  const dEn = typeof data.detail_en === "string" ? data.detail_en : "";

  // 规则：中文一律用 detail；其他语言用 detail_en；都要带 fallback
  if (isZh) return d || dEn || fallback;
  return dEn || d || fallback;
}

async function copyText(text) {
  if (!text) return false;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // fallthrough to execCommand
  }

  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (e) {
    return false;
  }
}

/**
 * SharedIDModal
 * - 外部按钮触发(open/onClose)
 * - 打开后请求 GET /web2/sharedid/
 * - 根据后端 code 决定展示内容
 */
export default function SharedIDModal({ open, onClose }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState(null); // { ok, code, detail, appleid, password }
  const [errMsg, setErrMsg] = useState("");
  const [copied, setCopied] = useState(""); // "appleid" | "password" | ""

  const code = resp?.code || "";
  const ok = !!resp?.ok;

  const title = useMemo(() => {
    if (loading) return t("clients.sharedid.title");
    if (ok && code === "OK") return t("clients.sharedid.title");
    if (code === "LOGIN_REQUIRED") return t("clients.sharedid.titleLoginRequired");
    if (code === "PAID_REQUIRED") return t("clients.sharedid.titlePaidRequired");
    if (code === "RENEW_REQUIRED") return t("clients.sharedid.titleRenewRequired");
    if (code === "NO_APPLE_ID") return t("clients.sharedid.titleNoAppleId");
    if (errMsg) return t("clients.sharedid.titleFetchFailed");
    return t("clients.sharedid.title");
  }, [loading, ok, code, errMsg, t]);

  useEffect(() => {
    let alive = true;

    async function fetchSharedId() {
      setLoading(true);
      setResp(null);
      setErrMsg("");
      setCopied("");

      try {
        // 你的 api client 里如果有 baseURL 前缀，这里用相对路径即可
        const r = await api.get("/sharedid/");
        if (!alive) return;
        setResp(r?.data || null);
      } catch (e) {
        if (!alive) return;

        const status = e?.response?.status;
        const data = e?.response?.data;

        // 后端在 401/403/404 也会返回结构化 body
        if (status && data && typeof data === "object") {
          setResp(data);
          setErrMsg("");
        } else {
          setErrMsg(t("clients.sharedid.errorNetwork"));
        }
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    }

    if (open) fetchSharedId();

    return () => {
      alive = false;
    };
  }, [open, t]);

  const detail = useMemo(() => {
    if (loading) return t("clients.sharedid.loading");
    if (errMsg) return errMsg;

    // 后端现在会返回 detail / detail_en：中文用 detail，其他语言用 detail_en
    if (resp && (resp.detail || resp.detail_en)) {
      return safeDetail(resp, "", i18n?.language);
    }

    // 兜底：如果后端没给 detail 字段（理论上不会），使用前端本地 i18n
    if (ok && code === "OK") return t("clients.sharedid.okHint");
    return "";
  }, [loading, errMsg, resp, ok, code, t, i18n?.language]);

  const showCredentials = ok && code === "OK";

  const handleCopy = async (field) => {
    const text = field === "appleid" ? resp?.appleid : resp?.password;
    const success = await copyText(text || "");
    setCopied(success ? field : "");
    if (success) {
      setTimeout(() => setCopied(""), 1200);
    }
  };

  const primaryAction = useMemo(() => {
    if (loading) return null;
    if (showCredentials) return null;

    if (code === "LOGIN_REQUIRED") {
      return {
        text: t("clients.sharedid.actionLogin"),
        onClick: () => {
          onClose();
          navigate("/signin/");
        },
      };
    }

    if (code === "PAID_REQUIRED" || code === "RENEW_REQUIRED") {
      return {
        text:
          code === "PAID_REQUIRED"
            ? t("clients.sharedid.actionBuy")
            : t("clients.sharedid.actionRenew"),
        onClick: () => {
          onClose();
          navigate("/userinfo/#pricing");
        },
      };
    }

    return null;
  }, [loading, showCredentials, code, navigate, onClose, t]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pr: 6 }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <CircularProgress size={20} />
            <MKTypography variant="body2">{detail}</MKTypography>
          </Box>
        ) : (
          <>
            {detail && !showCredentials ? (
              <MKTypography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {detail}
              </MKTypography>
            ) : null}

            {showCredentials ? (
              <Box sx={{ mt: 2 }}>
                {/* 多行重要说明（放在凭证区域内部，靠近账号密码） */}
                <MKTypography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    color: "error.main",
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  {t("clients.sharedid.tip")}
                </MKTypography>

                {/* 账号密码作为一个整体展示 */}
                <Box
                  sx={{
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: 1,
                    p: 1.1,
                  }}
                >
                  <MKTypography variant="caption" sx={{ opacity: 0.75 }}>
                    {t("clients.sharedid.labelAppleId")}
                  </MKTypography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.1 }}>
                    <MKTypography
                      variant="body1"
                      sx={{ wordBreak: "break-all", fontWeight: 700, lineHeight: 1.3 }}
                    >
                      {resp?.appleid || "-"}
                    </MKTypography>
                    <IconButton
                      aria-label="copy appleid"
                      size="small"
                      onClick={() => handleCopy("appleid")}
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                    {copied === "appleid" ? (
                      <MKTypography variant="caption" sx={{ opacity: 0.85 }}>
                        {t("clients.sharedid.copied")}
                      </MKTypography>
                    ) : null}
                  </Box>

                  <Divider sx={{ my: 0.75, opacity: 0.25 }} />

                  <MKTypography variant="caption" sx={{ opacity: 0.75 }}>
                    {t("clients.sharedid.labelPassword")}
                  </MKTypography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.1 }}>
                    <MKTypography
                      variant="body1"
                      sx={{ wordBreak: "break-all", fontWeight: 700, lineHeight: 1.3 }}
                    >
                      {resp?.password || "-"}
                    </MKTypography>
                    <IconButton
                      aria-label="copy password"
                      size="small"
                      onClick={() => handleCopy("password")}
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                    {copied === "password" ? (
                      <MKTypography variant="caption" sx={{ opacity: 0.85 }}>
                        {t("clients.sharedid.copied")}
                      </MKTypography>
                    ) : null}
                  </Box>
                </Box>

                {/* 图片说明（步骤截图） */}
                <Box sx={{ mt: 1.2 }}>
                  <MKTypography variant="caption" sx={{ opacity: 0.7, mb: 0.8 }}>
                    {t("clients.sharedid.imageHint")}
                  </MKTypography>

                  <Box
                    component="img"
                    src={iosIntro1}
                    alt="app store shared apple id guide"
                    sx={{
                      width: "100%",
                      borderRadius: 1,
                      border: "1px solid rgba(255,255,255,0.15)",
                      display: "block",
                      mx: "auto",
                    }}
                  />

                  <MKTypography
                    variant="body2"
                    sx={{ whiteSpace: "pre-wrap", opacity: 0.85, mt: 1 }}
                  >
                    {t("clients.sharedid.imageDesc")}
                  </MKTypography>
                </Box>
              </Box>
            ) : null}
          </>
        )}
      </DialogContent>

      <DialogActions>
        {primaryAction ? (
          <MKButton variant="gradient" color="info" onClick={primaryAction.onClick}>
            {primaryAction.text}
          </MKButton>
        ) : null}

        <MKButton variant="outlined" color="info" onClick={onClose}>
          {t("common.close")}
        </MKButton>
      </DialogActions>
    </Dialog>
  );
}

SharedIDModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
